import React, { useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Home, Trees, Plus, RotateCcw, ArrowLeft } from "lucide-react";

export const AddZoneDialog = ({
  open,
  onOpenChange,
  internalTypes,
  externalTypes,
  hiddenPresets,
  onRestore,
  onCreate,
}) => {
  const [kind, setKind] = useState(null);
  const [type, setType] = useState("");
  const [name, setName] = useState("");

  const close = (v) => {
    onOpenChange(v);
    if (!v) {
      setKind(null);
      setType("");
      setName("");
    }
  };

  const create = (e) => {
    e.preventDefault();
    if (!type) {
      toast.error("Scegli la tipologia");
      return;
    }
    onCreate({ kind, type, name: name.trim() || type });
    toast.success(`${name.trim() || type} aggiunto`);
    close(false);
  };

  const types = kind === "internal" ? internalTypes : externalTypes;

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent
        className="max-h-[90vh] overflow-y-auto rounded-none sm:max-w-md"
        data-testid="add-zone-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-display uppercase tracking-tight">
            {kind ? (kind === "internal" ? "Ambiente interno" : "Area esterna") : "Cosa vuoi aggiungere?"}
          </DialogTitle>
        </DialogHeader>

        {!kind && (
          <div className="space-y-3">
            <button
              data-testid="choose-internal"
              onClick={() => setKind("internal")}
              className="flex w-full items-center gap-3 border-2 border-[#1B2A20] px-4 py-4 text-left transition-colors hover:bg-[#1B2A20] hover:text-[#F4F7F2]"
            >
              <Home className="h-5 w-5 text-[#4E7C59]" />
              <span>
                <span className="block font-display text-base">Ambiente interno</span>
                <span className="label-xs text-muted-foreground">
                  pavimento, soffitto, pareti, perimetro
                </span>
              </span>
            </button>
            <button
              data-testid="choose-external"
              onClick={() => setKind("external")}
              className="flex w-full items-center gap-3 border-2 border-dashed border-[#1B2A20] px-4 py-4 text-left transition-colors hover:bg-[#1B2A20] hover:text-[#F4F7F2]"
            >
              <Trees className="h-5 w-5 text-[#8FBF6F]" />
              <span>
                <span className="block font-display text-base">Area esterna</span>
                <span className="label-xs text-muted-foreground">
                  superficie e perimetro; superfici verticali opzionali
                </span>
              </span>
            </button>

            {hiddenPresets.length > 0 && (
              <div className="border border-border bg-[#F3F6F1] p-3">
                <div className="label-xs mb-2 text-muted-foreground">Ambienti rimossi — ripristina</div>
                <div className="flex flex-wrap gap-2" data-testid="restore-rooms-list">
                  {hiddenPresets.map((r) => (
                    <button
                      key={r.key}
                      data-testid={`restore-room-${r.key}`}
                      onClick={() => {
                        onRestore(r.key);
                        toast.success(`${r.name} ripristinato in piantina`);
                        close(false);
                      }}
                      className="flex items-center gap-1.5 border border-[#1B2A20] bg-white px-2.5 py-1.5 font-mono-t text-xs transition-colors hover:bg-[#1B2A20] hover:text-[#F4F7F2]"
                    >
                      <RotateCcw className="h-3 w-3" /> {r.name}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {kind && (
          <form onSubmit={create} className="space-y-4">
            <div>
              <span className="label-xs text-muted-foreground">Tipologia</span>
              <div className="mt-2 flex flex-wrap gap-2">
                {types.map((t) => (
                  <button
                    key={t}
                    type="button"
                    data-testid={`zone-type-${t.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    onClick={() => {
                      setType(t);
                      if (!name) setName(t);
                    }}
                    className={`border px-3 py-1.5 font-mono-t text-xs transition-colors ${
                      type === t
                        ? "border-[#4E7C59] bg-[#4E7C59] text-white"
                        : "border-border bg-white hover:border-[#4E7C59]"
                    }`}
                  >
                    {t}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="label-xs text-muted-foreground">Nome (rinominabile)</label>
              <Input
                data-testid="input-zone-name"
                className="mt-1 rounded-none"
                placeholder="es. Camera matrimoniale, Terrazzo cucina"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>

            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setKind(null)}
                className="rounded-none"
                data-testid="zone-back-btn"
              >
                <ArrowLeft className="h-4 w-4" />
              </Button>
              <Button
                data-testid="btn-create-zone"
                type="submit"
                className="flex-1 rounded-none bg-[#1B2A20] text-[#F4F7F2] hover:bg-[#4E7C59]"
              >
                <Plus className="mr-2 h-4 w-4" /> Aggiungi al progetto
              </Button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};
